





















































































import Vue from 'vue';
import { BCol, BRow } from 'bootstrap-vue';
import ZenMultiselect from '@reciprocity/multiselect';
import { ZenFormGroup } from 'zenComponents';
import { isEmpty, get, cloneDeep, debounce } from 'lodash';
import { mapGetters } from 'vuex';
import { MetricOptions, DimensionOptions, DIMENSIONS } from '@/constants/DashboardConstants';
import { DimensionType } from '../types';
import { sortByLabel } from '@/utils/DashboardUtils';
import { GET_OBJECT_ATTRIBUTE_VALUES, GET_OBJECT_CUSTOM_ATTRIBUTES } from './ObjectAttributes.vue';
import { findFirstNotUsedAttributeOption, findUnusedAttributeOptions } from './ObjectAttributesUtils';

export default Vue.extend({
  name: 'MappedObjectAttributes',
  components: {
    ZenMultiselect,
    ZenFormGroup,
    BCol,
    BRow,
  },
  props: {
    selectedObject: {
      type: Object,
      required: true,
    },
    selectedObjectChanged: {
      type: String,
      default: '',
    },
    mappedObjectAttributes: {
      type: Array,
      default: () => [],
    },
    currentMappedObjects: {
      type: Array,
      required: true,
    },
    disabled: {
      type: Boolean,
      required: false,
    },
  },
  data() {
    return {
      items: cloneDeep(this.mappedObjectAttributes),
      options: [],
    };
  },
  computed: {
    ...mapGetters(['featureFlags']),
  },
  watch: {
    items: {
      async handler() {
        await Promise.all(
          this.items.map(async (item, index) => {
            await this.addAttributeOptions(item, index);
          })
        );
        const localItems = cloneDeep(this.items);
        localItems.forEach(i => {
          delete i.searchAttributeValuesString;
        });
        this.$emit('mappedObjectAttributesChanged', localItems);
      },
      immediate: true,
      deep: true,
    },
    selectedObjectChanged() {
      this.items = [];
    },
  },
  created() {
    this.items = [];
    this.mappedObjectAttributes.forEach(attribute => {
      this.addExistingAttribute(attribute);
    });
  },
  methods: {
    async addNewAttribute() {
      this.options.push({
        attributeOptions: [],
        attributeValueOptions: [],
        attributeOptionsLoading: true,
        attributeValueOptionsLoading: true,
      });

      const objectAttributeOptions = await this.objectAttributeOptions(
        this.currentMappedObjects[0],
        this.options.length - 1
      );

      this.items.push({
        object: this.currentMappedObjects[0],
        name: findFirstNotUsedAttributeOption(this.items, objectAttributeOptions, DimensionType.MAPPING_ATTR),
        values: [],
        searchAttributeValuesString: '',
      });
    },
    addExistingAttribute(attribute: Object) {
      this.options.push({
        attributeOptions: [],
        attributeValueOptions: [],
        attributeOptionsLoading: true,
        attributeValueOptionsLoading: true,
      });
      this.items.push({
        ...attribute,
        searchAttributeValuesString: '',
      });
    },
    removeAttribute(index: number) {
      this.items.splice(index, 1);
      this.options.splice(index, 1);
    },
    async addAttributeOptions(item, index) {
      if (this.options[index]) {
        this.options[index].attributeOptions = await this.objectAttributeOptions(item.object, index);
        this.options[index].attributeValueOptions = await this.objectAttributeValuesOptions(item, index);
      }
    },
    async objectAttributeOptions(object, index) {
      let dimensions = [];
      let fase4Enabled = this.featureFlags.dashboards_expansion_phase4;
      if (!isEmpty(object)) {
        // get the correct dimensions
        dimensions = get(MetricOptions, `${object.value}.dimensions`, []).reduce((dimensions, dimension) => {
          if (!fase4Enabled) {
            if (dimension !== DIMENSIONS.TAG) {
              dimensions[dimension] = DimensionOptions[dimension];
            }
          } else {
            dimensions[dimension] = DimensionOptions[dimension];
          }
          return dimensions;
        }, {});
        dimensions = Object.entries(dimensions)
          .map(([value, options]) => ({ value, label: options.label, type: DimensionType.MAPPING_ATTR }))
          .sort(sortByLabel);

        const response = await this.$apollo.query({
          query: GET_OBJECT_CUSTOM_ATTRIBUTES,
          variables: {
            metric: object.value,
          },
        });

        const customAttributes = response.data.dashboardAttrs.customAttributes.map(c => ({
          value: c.value,
          label: c.label,
          customAttrType: c.type,
          type: DimensionType.CUSTOM_ATTRIBUTE,
        }));

        dimensions = [
          {
            dimensionsGroup: 'Attributes',
            dimensions,
          },
          {
            dimensionsGroup: 'Custom Attributes',
            dimensions: customAttributes,
          },
        ];
      }
      this.options[index].attributeOptionsLoading = false;
      return dimensions;
    },
    async objectAttributeValuesOptions(item, index) {
      if (isEmpty(item.name) || isEmpty(item.object)) return [];

      let variables = {
        dimension: item.name.value,
      };

      if (item.name.type === DimensionType.CUSTOM_ATTRIBUTE) {
        variables = {
          dimension: DimensionType.CUSTOM_ATTRIBUTE,
          customAttributeId: item.name.value.toString(),
        };
      }

      variables = {
        ...variables,
        metric: item.object.value,
        searchText: item.searchAttributeValuesString,
      };

      const response = await this.$apollo.query({
        query: GET_OBJECT_ATTRIBUTE_VALUES,
        variables,
      });

      this.options[index].attributeValueOptionsLoading = false;

      return response.data.dashboardFilters.data.map(c => ({
        value: c.value,
        label: this.stripHtml(c.label), // replace html tags with empty strings
      }));
    },
    debounceSearchAttributeValues: debounce(function(searchString: string, index: number) {
      this.searchAttributeValues(searchString, index);
    }, 600),
    searchAttributeValues(searchString: string, index: number) {
      const search = searchString.trim();
      if (this.items[index].searchAttributeValuesString !== search) {
        this.options[index].attributeValueOptionsLoading = true;
        this.items[index].searchAttributeValuesString = search;
      }
    },
    filterAttributeOptions(options: any[], index: number) {
      return findUnusedAttributeOptions(this.items, options, index, DimensionType.MAPPING_ATTR);
    },
    stripHtml(label: string) {
      return label.replace(/(<([^>]+)>)/gi, ' ');
    },
  },
});
